/**
 * How the system chooses an instrument, and whether it may start now.
 *
 * Two questions, deliberately not one. The score answers "how good is this
 * instrument"; the entry guard answers "may a new long be opened in it at this
 * moment". A high score with the entry blocked is a normal, expected state —
 * the instrument is good and the price is extended — and an interface that
 * folded one into the other would either hide a good instrument or invite an
 * entry the runtime is refusing.
 *
 * The score itself is split for the same reason. Version 1 mixed quality with
 * how much history had been accumulated, so a promising instrument with three
 * weeks of data and a mediocre one with a year could land on the same number
 * and nothing said which was which. Version 2 states them apart: a score, and
 * an evidence level beside it. The evidence never moves the number.
 *
 * A Score Policy is its own contract. It versions apart from the Strategy
 * Contract, the Capital Policy and the Allocation Policy, because changing how
 * instruments are chosen is not changing how a cycle trades — and a shared
 * version number would say it was.
 *
 * Everything here is a read contract. Nothing computes a score, a contribution
 * or a verdict: those are the server's, and a browser that recomputed them
 * would be a second opinion about a decision that has already been taken.
 */
import { CONTRACTS, type Contract, type Parameter, type Provenance } from "./contracts";

/** How much history stands behind a score. Never part of the score itself. */
export type ScoreEvidenceLevel = "COLD_START" | "LIMITED" | "ESTABLISHED";

export const EVIDENCE_LABEL: Record<ScoreEvidenceLevel, string> = {
  COLD_START: "холодный старт",
  LIMITED: "ограниченная история",
  ESTABLISHED: "история накоплена",
};

/**
 * The level the server named, or nothing.
 *
 * Unknown values are returned as they arrived rather than mapped to the
 * nearest known one: the enum is the server's and this build may be older
 * than it.
 */
export function evidenceLabel(raw: string | null | undefined): string | null {
  if (raw == null || raw === "") return null;
  return raw in EVIDENCE_LABEL ? EVIDENCE_LABEL[raw as ScoreEvidenceLevel] : raw;
}

/** Whether a new long may be opened right now. */
export type EntryRegimeVerdict = "PASS" | "BLOCKED" | "UNKNOWN";

export const VERDICT_LABEL: Record<EntryRegimeVerdict, string> = {
  PASS: "вход разрешён",
  BLOCKED: "вход заблокирован",
  UNKNOWN: "недостаточно данных",
};

/**
 * How loudly each verdict reads.
 *
 * `UNKNOWN` is not neutral. Admission is fail-closed, so an unknown regime
 * stops a new cycle exactly as a block does, and drawing it as a shrug would
 * misreport what the runtime is doing.
 */
export const VERDICT_TONE: Record<EntryRegimeVerdict, "ready" | "warning" | "danger"> = {
  PASS: "ready",
  BLOCKED: "danger",
  UNKNOWN: "warning",
};

export function verdictOf(raw: string | null | undefined): EntryRegimeVerdict | null {
  if (raw === "PASS" || raw === "BLOCKED" || raw === "UNKNOWN") return raw;
  return null;
}

/** Why the guard said what it said, in the reader's words. */
export const REASON_LABEL: Record<string, string> = {
  OVEREXTENDED: "цена перегрета относительно недельного диапазона",
  INSUFFICIENT_HISTORY: "недостаточно завершённых свечей",
  STALE_DATA: "данные устарели",
  STALE_PRICE: "котировка устарела или не подтверждена по времени",
  INVALID_RANGE: "диапазон недели непригоден для расчёта",
  PREVIEW_DEFERRED: "решение отложено до входа: проверка на свежей цене",
  STILL_FALLING: "падение не остановилось: последняя свеча закрылась ниже предыдущей",
  BELOW_LAST_CLOSE: "цена ниже последнего закрытия: разворот не подтверждён",
  NO_REBOUND: "отскок от локального минимума меньше 2%",
};

export function reasonLabel(raw: string | null | undefined): string | null {
  if (raw == null || raw === "") return null;
  return REASON_LABEL[raw] ?? raw;
}

/**
 * Why the survey would not rank an instrument at all, in the reader's words.
 *
 * A different question from the entry guard's above: that one asks whether a
 * long may be opened right now, this one asks why the contract was never
 * scored. The two used to be indistinguishable on screen because neither was
 * shown — an instrument the strategy had declined simply vanished, and the
 * fifty it is holding vanished with the rest.
 *
 * The code travels and the sentence is written here, so a journal and a screen
 * can still be searched with the same word.
 */
export const REFUSAL_LABEL: Record<string, string> = {
  ALREADY_HELD: "уже в позиции",
  COOLDOWN: "пауза после закрытия",
  QUARANTINED: "карантин",
  BLACKLISTED: "в чёрном списке",
  NOT_TRADABLE: "биржа не даёт торговать",
  ILLIQUID: "оборот ниже порога",
  SPREAD_TOO_WIDE: "спред слишком широкий",
  TOO_FEW_PULLBACKS: "слишком редкие откаты",
  RECOVERS_TOO_RARELY: "редко восстанавливается после падения",
  PERSISTENT_DECLINE: "устойчивое падение",
  TOO_LITTLE_HISTORY: "мало истории",
};

export function refusalLabel(raw: string): string {
  return REFUSAL_LABEL[raw] ?? raw;
}

/** One factor of a profile: what it measures and how much it counts. */
export type ScoreFeature = {
  /** The server's key for the factor, and this build's join to it. */
  readonly key: string;
  readonly label: string;
  /** The share of the score, already formatted for reading. */
  readonly weight: string;
  /** Which way is better, so a reader knows what a high raw value means. */
  readonly direction: "higher" | "lower";
  readonly normalization?: string;
  readonly minimum?: string;
  readonly maximum?: string;
  readonly note?: string;
};

export type ScoreProfile = {
  readonly pool: string;
  /** What the profile is trying to buy, in one line. */
  readonly purpose: string;
  readonly features: readonly ScoreFeature[];
};

/** One symbol's factor, as the server decomposed it. */
export type ScoreContribution = {
  readonly key: string;
  readonly raw_value: string | null;
  readonly normalized_value: string | null;
  readonly weight: string | null;
  /** The server's own arithmetic. This build formats it and never derives it. */
  readonly contribution: string | null;
};

/** The guard's reading for one symbol. */
export type EntryRegimeReading = {
  readonly version: string | null;
  readonly candle_cutoff: string | null;
  readonly range_position: string | null;
  readonly rise_from_low: string | null;
  readonly verdict: EntryRegimeVerdict;
  readonly reason: string | null;
};

/** What the server says is choosing instruments right now. */
export type ScorePolicy = {
  readonly policy_version: string;
  readonly score_version: string;
  readonly guard_version: string;
  readonly status: "ACTIVE" | "SUPERSEDED" | "DRAFT";
  readonly activated_at: string | null;
  readonly provenance: Provenance;
  readonly profiles: readonly ScoreProfile[];
  readonly guard_rule: string | null;
  readonly guard_freshness_seconds: number | null;
  /** The guard's thresholds, as the policy states them. */
  readonly guard: readonly ScoreFeature[];
};

/**
 * The Score v2 contract as approved, held until the server reports its own.
 *
 * Declared, and marked so. The page may say what the contract specifies and may
 * not say that the runtime is choosing instruments this way — those are
 * different claims, and only the second one describes decisions that have
 * already been taken.
 */
export const SCORE_POLICY: ScorePolicy = {
  policy_version: "SP v1",
  score_version: "skryptos-score-2",
  guard_version: "entry-regime-1",
  status: "DRAFT",
  activated_at: null,
  provenance: "declared",
  guard_rule: null,
  guard_freshness_seconds: null,
  profiles: [
    {
      pool: "PRIME",
      purpose: "устойчивое восстановление и меньший риск долгой блокировки капитала",
      features: [
        { key: "recovery_rate", label: "Доля восстановлений", weight: "30 %", direction: "higher" },
        { key: "recovery_speed", label: "Скорость восстановления", weight: "25 %", direction: "higher" },
        { key: "decline_stability", label: "Устойчивость снижения", weight: "20 %", direction: "higher" },
        { key: "net_per_capital_day", label: "NET на капитало-день", weight: "15 %", direction: "higher" },
        { key: "pullback_frequency", label: "Частота откатов", weight: "10 %", direction: "higher" },
      ],
    },
    {
      pool: "PULSE",
      purpose: "быстрый оборот капитала при приемлемом качестве восстановления",
      features: [
        { key: "recovery_speed", label: "Скорость восстановления", weight: "30 %", direction: "higher" },
        { key: "net_per_capital_day", label: "NET на капитало-день", weight: "25 %", direction: "higher" },
        { key: "pullback_frequency", label: "Частота откатов", weight: "20 %", direction: "higher" },
        { key: "recovery_rate", label: "Доля восстановлений", weight: "15 %", direction: "higher" },
        { key: "decline_stability", label: "Устойчивость снижения", weight: "10 %", direction: "higher" },
      ],
    },
  ],
  guard: [
    {
      key: "window",
      label: "Окно",
      weight: "168 завершённых часовых свечей",
      direction: "higher",
      note: "неделя истории; незавершённая свеча не считается",
    },
    {
      key: "range_position",
      label: "Позиция в диапазоне",
      weight: "≥ 90 %",
      direction: "lower",
      note: "где закрытие относительно недельных максимума и минимума",
    },
    {
      key: "rise_from_low",
      label: "Рост от минимума",
      weight: "≥ 20 %",
      direction: "lower",
      note: "блокировка только при выполнении обоих условий сразу",
    },
    {
      key: "unknown",
      label: "Неизвестный режим",
      weight: "вход не открывается",
      direction: "lower",
      note: "fail-closed: неизвестность останавливает новый цикл так же, как блокировка",
    },
  ],
};

/**
 * The Score Policy as a versioned contract, for the history and the difference.
 *
 * The same mechanism serves all four policies: a version, a diff against the
 * one before it, and a count of what ran under it. Building it from the policy
 * above rather than writing the numbers twice means the tab and the history can
 * never disagree about what the weights are.
 */
function weightParameters(policy: ScorePolicy): readonly Parameter[] {
  const profiles = policy.profiles.flatMap((profile) =>
    profile.features.map(
      (feature): Parameter => ({
        id: `${profile.pool.toLowerCase()}_${feature.key}`,
        label: `${profile.pool} · ${feature.label}`,
        value: feature.weight,
        provenance: policy.provenance,
      }),
    ),
  );
  const guard = policy.guard.map(
    (rule): Parameter => ({
      id: `guard_${rule.key}`,
      label: `Фильтр · ${rule.label}`,
      value: rule.weight,
      provenance: policy.provenance,
      ...(rule.note === undefined ? {} : { note: rule.note }),
    }),
  );
  return [...profiles, ...guard];
}

export function scoreContractFrom(policy: ScorePolicy): Contract {
  return {
    kind: "score",
    title: "Политика отбора",
    version: policy.policy_version,
    decides: "как система выбирает инструмент и когда разрешает вход",
    provenance: policy.provenance,
    parameters: weightParameters(policy),
  };
}

export const SCORE_CONTRACT: Contract = scoreContractFrom(SCORE_POLICY);

/**
 * Every policy that versions on its own.
 *
 * The three that define how the account trades, and the one that defines what
 * it trades. They are listed together only where the mechanism is shared —
 * version history and the difference shown before a change — never where the
 * page has to explain what one of them decides.
 */
export const POLICIES: readonly Contract[] = [...CONTRACTS, SCORE_CONTRACT];

/** One symbol's score reading, as far as the server reports it. */
export type ScoreReading = {
  readonly score: number | null;
  readonly score_version: string | null;
  readonly evidence_level: string | null;
  readonly pool: string | null;
  readonly rank: number | null;
  readonly contributions: readonly ScoreContribution[];
  readonly regime: EntryRegimeReading | null;
};

/**
 * The score reading a ranking payload carries.
 *
 * Read defensively: `score_version` is in the schema today, the evidence level,
 * the pool, the decomposition and the guard are not. Widening the generated
 * type would be this build claiming a promise the server has not made, and
 * inventing a pool would be worse — the ranking scores instruments, and which
 * pool a cycle runs in is decided when a slot is opened.
 */
export function scoreOf(candidate: object): ScoreReading {
  const record = candidate as Record<string, unknown>;
  const text = (key: string): string | null => {
    const raw = record[key];
    return typeof raw === "string" && raw !== "" ? raw : null;
  };
  const count = (key: string): number | null => {
    const raw = record[key];
    return typeof raw === "number" ? raw : null;
  };
  return {
    score: count("skryptos_score"),
    score_version: text("score_version"),
    evidence_level: text("evidence_level"),
    pool: text("logical_pool") ?? text("pool"),
    rank: count("rank"),
    contributions: contributionsOf(record.contributions),
    regime: regimeOf(record.entry_guard ?? record.entry_regime),
  };
}

function contributionsOf(raw: unknown): readonly ScoreContribution[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry): ScoreContribution[] => {
    if (typeof entry !== "object" || entry === null) return [];
    const record = entry as Record<string, unknown>;
    const key = record.feature ?? record.key;
    if (typeof key !== "string" || key === "") return [];
    const text = (value: unknown): string | null =>
      typeof value === "string" && value !== "" ? value : null;
    return [
      {
        key,
        raw_value: text(record.raw_value),
        normalized_value: text(record.normalized_value),
        weight: text(record.applied_weight ?? record.weight),
        contribution: text(record.contribution),
      },
    ];
  });
}

export function regimeOf(raw: unknown): EntryRegimeReading | null {
  if (typeof raw !== "object" || raw === null) return null;
  const record = raw as Record<string, unknown>;
  const verdict = verdictOf(typeof record.verdict === "string" ? record.verdict : null);
  if (verdict === null) return null;
  const text = (key: string): string | null => {
    const value = record[key];
    return typeof value === "string" && value !== "" ? value : null;
  };
  return {
    version: text("version"),
    candle_cutoff: text("candle_cutoff"),
    range_position: text("range_position"),
    rise_from_low: text("rise_from_low"),
    verdict,
    reason: text("reason"),
  };
}

/** The words used everywhere for a figure the server has not sent. */
export const UNREPORTED = "не сообщается";

/**
 * Whether a slot could take this instrument right now, in one reading.
 *
 * The status and the entry guard are two separate facts and the screens showed
 * them as two separate marks — a green "Готова" badge with a small outlined
 * "вход закрыт" beside it. Two opposite statements on one row, and the reader
 * has no rule for which wins.
 *
 * The guard wins, because `admission_allowed` requires it to pass: a blocked
 * instrument cannot be entered whatever its status says, so a badge reading
 * "Готова" on one is not a nuance, it is wrong. It matters beyond this screen
 * — NOTUSDT sat at rank 8 with the guard blocking it, which puts it inside the
 * preview on Обзор where the small badge is not rendered at all and "Готова"
 * was the only thing a reader saw.
 *
 * Only READY is overruled. A cooling or quarantined instrument is unavailable
 * for its own reason, and replacing that reason with the guard's would answer
 * a question the reader did not ask.
 */
export type Availability = {
  readonly status: string;
  readonly label: string;
  /** Why entry is closed, when the guard is what closed it. */
  readonly reason: string | null;
};

/** The status a row should show, once the guard has had its say. */
export function availabilityOf(
  status: string,
  verdict: EntryRegimeVerdict | null,
  reason: string | null | undefined,
): Availability {
  if (status !== "READY" || verdict !== "BLOCKED") {
    return { status, label: STATUS_LABEL[status] ?? status, reason: null };
  }
  return {
    status: "ENTRY_BLOCKED",
    label: "Вход закрыт",
    reason: reasonLabel(reason),
  };
}

/** What each status is called, in one place for every screen that shows one. */
export const STATUS_LABEL: Record<string, string> = {
  READY: "Готова",
  ACTIVE: "В работе",
  COOLDOWN: "Отдыхает",
  QUARANTINED: "Карантин",
  QUARANTINED_REVIEW_REQUIRED: "Карантин · нужна проверка",
  BLOCKED: "Отклонена",
  ENTRY_BLOCKED: "Вход закрыт",
};
